/**
 * 블로그가 글을 내는 언어 — 웹에 경로가 있는 언어만 둔다.
 * @description 원문은 영어이고 접두사 없이 `/blog/{slug}` 에 선다. 번역은 언어 코드가 앞에
 *   붙는다(`/es/blog/{slug}`). 서버는 코드를 대문자(`"ES"`)로 주고 주소는 소문자를 쓴다.
 *
 *   여기 없는 언어가 서버에서 오면 원문으로 본다 — 경로가 없는 언어로는 링크를 만들 수 없다.
 *   언어를 늘리려면 여기에 더하고 `app/{언어}/blog/[slug]` 를 세운 **뒤에** 서버의
 *   `languages.blog_enabled` 를 켠다. 순서가 뒤집히면 hreflang 이 열리지 않는 주소를 가리킨다.
 */
export const BLOG_LANGUAGES = ["en", "es"] as const;

export type BlogLanguage = (typeof BLOG_LANGUAGES)[number];

/** 원문 언어 — 번역이 없거나 공개 전이면 서버가 이 언어로 준다 */
export const ORIGINAL_BLOG_LANGUAGE: BlogLanguage = "en";

/**
 * 언어 전환 링크에 적는 이름 — 그 언어로 쓴 제 이름이다. 스페인어 화면에서 영어를 찾는 사람도,
 * 영어 화면에서 스페인어를 찾는 사람도 제 언어 이름은 알아본다.
 */
export const BLOG_LANGUAGE_NAMES: Record<BlogLanguage, string> = {
  en: "English",
  es: "Español",
};

const isBlogLanguage = (code: string): code is BlogLanguage =>
  (BLOG_LANGUAGES as readonly string[]).includes(code);

/**
 * 서버가 준 언어 코드(`"ES"`)를 웹의 언어로 읽는다. 웹에 경로가 없는 언어면 `null`.
 * @description `es-ES` 처럼 지역이 붙어 와도 앞의 언어만 본다 — 블로그 경로는 언어마다 하나다.
 *   `availableLanguages` 에서 링크를 만들 언어를 고를 때 쓴다.
 */
export function parseBlogLanguage(
  code: string | null | undefined,
): BlogLanguage | null {
  const language = code?.split("-")[0]?.toLowerCase() ?? "";
  return isBlogLanguage(language) ? language : null;
}

/**
 * 글이 실제로 나간 언어(`BlogItem.language`)를 웹의 언어로 읽는다.
 * @description 비어 오면(번역 대상이 아닌 앱 글) 원문이다. 웹은 경로가 있는 언어로만 서버에
 *   묻기 때문에, 모르는 언어가 이 자리에 올 일은 없다 — 와도 원문 경로로 접는다.
 */
export function toBlogLanguage(code: string | null | undefined): BlogLanguage {
  return parseBlogLanguage(code) ?? ORIGINAL_BLOG_LANGUAGE;
}

/**
 * 서버의 `availableLanguages`(`["EN", "ES"]`) 중 웹에 경로가 있는 언어만, 중복 없이.
 * @description 서버가 웹보다 먼저 언어를 켜도 열리지 않는 주소로 링크하지 않게 거른다.
 */
export const linkableBlogLanguages = (
  codes: readonly string[],
): BlogLanguage[] => [
  ...new Set(
    codes
      .map(parseBlogLanguage)
      .filter((language): language is BlogLanguage => language !== null),
  ),
];
